import React from "react"
import useOutsideClick from "../hooks/useOutsideClick";
import IconFont from "./IconFont";

interface ContextMenuProps {
  children: React.ReactNode
  className?: string
  buttonText?: string
  ariaLabel?: string
}

function ContextMenu({ children, className, buttonText, ariaLabel }: ContextMenuProps) {

  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [menuPosition, setMenuPosition] = React.useState<React.CSSProperties>({
    right: 0,
    top: "calc(100% + 4px)",
  })
  const [menuWidth, setMenuWidth] = React.useState<number | null>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  const ref = useOutsideClick(() => {
    setIsOpen(false)
  }) as React.RefObject<HTMLDetailsElement>;

  const handleMenuItemClick = () => {
    setIsOpen(false)
  }

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsOpen((current) => !current)
  }

  React.useLayoutEffect(() => {
    if (!isOpen || !ref.current || !menuRef.current) return

    const detailsRect = ref.current.getBoundingClientRect()
    const menuRect = menuRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const offset = 4
    const edgeGap = 8
    const nextPosition: React.CSSProperties = {}

    if (detailsRect.right - menuRect.width < edgeGap && detailsRect.left + menuRect.width <= viewportWidth - edgeGap) {
      nextPosition.left = 0
    } else {
      nextPosition.right = 0
    }

    if (detailsRect.bottom + offset + menuRect.height > viewportHeight - edgeGap && detailsRect.top - offset - menuRect.height >= edgeGap) {
      nextPosition.bottom = "calc(100% + 4px)"
    } else {
      nextPosition.top = "calc(100% + 4px)"
    }

    setMenuPosition(nextPosition)
    setMenuWidth(Math.ceil(menuRect.width))
  }, [isOpen, ref])

  return (
    <div className={`flex items-center ${className || ""}`}>
      <details ref={ref} className="relative inline-flex items-center" open={isOpen}>
        <summary className="flex list-none items-center [&::-webkit-details-marker]:hidden">
          <button
            type="button"
            aria-haspopup="menu"
            aria-label={ariaLabel ?? buttonText ?? "More actions"}
            aria-expanded={isOpen}
            className="flex items-center justify-center p-0"
            onClick={handleOnClick}
          >
            {buttonText ? <span className="text-sm text-body-muted px-[16px] py-[8px] border border-border-input rounded-md">{buttonText}</span> : <span className="inline-flex h-8 w-8 items-center justify-center"><IconFont name="ellipsis-vertical" size={24} /></span>}
          </button>
        </summary>
        <div
          ref={menuRef}
          role="menu"
          className={`ui-context-menu-content absolute rounded-[6px] border border-border-default bg-background-surface-02 p-[16px] shadow-[0_4px_20px_0_var(--ui-shadow-xl)] ${isOpen ? "ui-layer-popover flex h-auto min-w-[144px] max-w-[calc(100vw-16px)] cursor-default whitespace-nowrap" : "m-0 h-[20px] w-[20px] cursor-pointer overflow-hidden [clip:rect(0_0_0_0)] [clip-path:inset(50%)]"}`}
          style={isOpen ? { ...menuPosition, width: menuWidth ? `${menuWidth}px` : undefined } : undefined}
          onClick={handleMenuItemClick}
        >
          <div className="ui-context-menu-list flex w-full flex-col gap-[8px]">
            {children}
          </div>
        </div>
      </details>
    </div>
  )
}

export default ContextMenu
